import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "./BlogPost.scss";
import { useLanguage } from "../../i18n/LanguageContext";
import { footer, blogPostUi } from "../../i18n/content";
import { fetchBlogPost } from "../../lib/blogApi";
import { urlForImage } from "../../lib/sanityClient";
import elinaMyshanichAward from "../../assets/BlogPost/elina-myshanich-award.jpg";

const BlogPost = () => {
  const { year } = useParams();
  const { t } = useLanguage();
  const [post, setPost] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    setLoaded(false);
    fetchBlogPost(year)
      .then((data) => {
        if (active) {
          setPost(data);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (active) {
          setPost(null);
          setLoaded(true);
        }
      });
    return () => {
      active = false;
    };
  }, [year]);

  if (!loaded) {
    return <div className="blogpost_test_wrapper" />;
  }

  if (!post) {
    return (
      <div className="blogpost_test_wrapper">
        <div className="lw-wrap" style={{ padding: "4rem 0" }}>
          <Link className="lwt-back-link" to="/blog">
            {t(blogPostUi.allStories)}
          </Link>
          <h1>Story not found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="blogpost_test_wrapper">
      <header className="lw-post-hero">
        <div className="lw-wrap">
          <Link className="lwt-back-link" to="/blog">
            {t(blogPostUi.allStories)}
          </Link>
          <div className="lw-eyebrow">
            <span className="lw-dot" />
            {t(post.kicker)}
          </div>
          <h1>{t(post.title)}</h1>
          <div className="lwt-byline">
            <img src={urlForImage(post.bylinePhoto, { width: 200, height: 200 })} alt={t(post.bylineName)} />
            <div>
              <div className="lwt-byline-name">{t(post.bylineName)}</div>
              <div className="lwt-byline-role">{t(post.bylineRole)}</div>
            </div>
          </div>
        </div>
      </header>

      {year !== "2025-Exclusive-interview-with-the-Best-Director-nomination-winner-elina-myshanich" && (
        <div className="lwt-post-cover">
          <img src={urlForImage(post.coverImage, { width: 1600 })} alt={t(post.title)} />
        </div>
      )}

      <section className="lwt-post-body">
        <div className="lw-wrap">
          {(post.paragraphs || []).map((p, i) => {
            const paragraphs = post.paragraphs || [];
            const showMidImage =
              year === "2025-Exclusive-interview-with-the-Best-Director-nomination-winner-elina-myshanich" &&
              i === Math.floor(paragraphs.length / 2);
            return (
              <React.Fragment key={i}>
                {showMidImage && (
                  <img
                    className="lwt-post-mid-image"
                    src={urlForImage(post.coverImage, { width: 1200 })}
                    alt={t(post.title)}
                  />
                )}
                <p>{t(p)}</p>
              </React.Fragment>
            );
          })}
        </div>
      </section>

      {year === "2025-Exclusive-interview-with-the-Best-Director-nomination-winner-elina-myshanich" && (
        <section className="lwt-post-photo">
          <div className="lw-wrap">
            <img
              src={elinaMyshanichAward}
              alt="Elina Myshanich with her Best Director nomination certificate"
            />
          </div>
        </section>
      )}

      {year === "2023-recap-going-international" && (
        <section className="lwt-post-youtube">
          <div className="lw-wrap">
            <div className="lwt-youtube-frame">
              <iframe
                src="https://www.youtube.com/embed/jdlv60xtg-8"
                title="Adjarian Film Tales 2023 Recap"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {year === "2025-edition" && (
        <section className="lwt-post-youtube">
          <div className="lw-wrap">
            <div className="lwt-youtube-frame">
              <iframe
                src="https://www.youtube.com/embed/ShBNg7WlWUs"
                title="Adjarian Film Tales 2025 Edition"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {year === "2025" && (
        <section className="lwt-post-youtube">
          <div className="lw-wrap">
            <div className="lwt-youtube-frame">
              <iframe
                src="https://www.youtube.com/embed/efSXK2m65WI"
                title="Adjarian Film Tales 2025"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {year === "2026" && (
        <section className="lwt-post-youtube">
          <div className="lw-wrap">
            <div className="lwt-youtube-frame">
              <iframe
                src="https://www.youtube.com/embed/K5yCJFPrgXg"
                title="Adjarian Film Tales 2026"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      {post.hasWinners && (
        <section className="lwt-winners">
          <div className="lw-wrap">
            <div className="lw-section-head">
              <span className="lw-kicker">{t(blogPostUi.winnersKicker)}</span>
              <h2 className="lw-h-title">{t(post.winnersTitle)}</h2>
              <p className="lwt-winners-note">{t(post.winnersNote)}</p>
            </div>
            <div className="lwt-winners-list">
              {(post.winners || []).map((w, i) => (
                <div className="lwt-winner-row" key={i}>
                  <span className="lwt-winner-category">{t(w.category)}</span>
                  <span className="lwt-winner-name">{t(w.name)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {post.hasJury && (
        <section className="lwt-jury">
          <div className="lw-wrap">
            <div className="lw-section-head">
              <span className="lw-kicker">{t(blogPostUi.juryKicker)}</span>
              <h2 className="lw-h-title">{t(post.juryTitle)}</h2>
            </div>
            <div className="lwt-jury-grid">
              {(post.jury || []).map((j, i) => (
                <div className="lwt-jury-card" key={i}>
                  <div className="lwt-jury-photo">
                    <img src={urlForImage(j.photo, { width: 400, height: 500 })} alt={t(j.name)} />
                  </div>
                  <div className="lwt-jury-meta">
                    <h3>{t(j.name)}</h3>
                    <p>{t(post.juryMemberLabel)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {post.hasGallery && (
        <section className="lwt-gallery">
          <div className="lw-wrap">
            <div className="lw-section-head">
              <span className="lw-kicker">{t(post.galleryKicker)}</span>
              <h2 className="lw-h-title">{t(post.galleryTitle)}</h2>
            </div>
            <div className="lwt-gallery-grid">
              {(post.gallery || []).map((img, i) => (
                <div className="lwt-gallery-item" key={i}>
                  <img src={urlForImage(img, { width: 800, height: 600 })} alt="" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {year.startsWith("2022") && (
        <section className="lwt-post-video">
          <div className="lw-wrap">
            <a
              className="lw-btn"
              href="https://www.facebook.com/vaxo.gograchadze/videos/507533838091876/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(blogPostUi.watchRecap)}
            </a>
          </div>
        </section>
      )}

      {year.startsWith("2025") && (
        <section className="lwt-post-video">
          <div className="lw-wrap">
            <a
              className="lw-btn"
              href="https://www.facebook.com/watch/?v=1035342002472147"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(blogPostUi.watchRecap)}
            </a>
          </div>
        </section>
      )}

      <section className="lwt-post-foot">
        <div className="lw-wrap">
          <Link className="lw-btn-ghost" to="/blog">
            {t(blogPostUi.allStories)}
          </Link>
          <Link className="lw-btn" to="/submission">
            {t(blogPostUi.submitFilm)}
          </Link>
        </div>
      </section>

      <footer className="lw-footer">
        <div className="lw-wrap lw-foot-bottom">
          <span>{t(footer.est)}</span>
          <Link to="/">{t(footer.backHome)}</Link>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
